import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Loader2, Plus, KeyRound, Trash2, ShieldCheck, Pencil, Eye, Send, Briefcase } from "lucide-react";
import ConsoleShell, { Breadcrumb } from "./ConsoleShell";
import { API, useAuth } from "./AuthContext";

const ROLES = [
  { value: "reviewer", label: "Reviewer · read-only", hint: "Sees every engagement, cannot change anything. For BD / Medical Affairs / clients.", Icon: Eye },
  { value: "operator", label: "Operator · read + write", hint: "Runs prompts, commits signals, triangulates. Cannot create/delete engagements or manage users.", Icon: Pencil },
  { value: "bd",       label: "Business Development · Outreach + demo", hint: "Prospect Outreach and the guided client demo only (illustrative cases). No engagement console, no live client data.", Icon: Briefcase },
  { value: "admin",    label: "Admin · full control",   hint: "Everything, including engagements, briefing requests and user accounts.", Icon: ShieldCheck },
];
const ROLE_CLS = {
  admin:    "text-[#0F1825] bg-[#F0EBDC] border-[#0F1825]/30",
  operator: "text-[#C84C21] bg-[#FCE9E1] border-[#C84C21]/40",
  reviewer: "text-[#2E4A38] bg-[#EAF3EC] border-[#3F6D55]/40",
  bd:       "text-[#2E4A38] bg-[#EAF3EC] border-[#3F6D55]/40",
};
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Request failed.";
};
const fmtDate = (ts) => (ts ? new Date(ts).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const inputCls = "w-full bg-white border border-[#E8E1D2] px-3 py-2.5 text-[13px] text-[#0F1825] focus:outline-none focus:border-[#0F1825]";
const labelCls = "block font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680] mb-1.5";

function RoleChip({ role }) {
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.16em] ${ROLE_CLS[role] || ROLE_CLS.reviewer}`}>
      {role}
    </span>
  );
}

function AddUserForm({ onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: "reviewer", password: "", outreach_access: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/users`, form);
      toast.success(`${data.email} can now log in as ${data.role}${data.outreach_access ? " · with Prospect Outreach" : ""}`);
      setForm({ email: "", name: "", role: "reviewer", password: "", outreach_access: false });
      onCreated(data);
    } catch (err) {
      toast.error(errText(err));
    } finally { setBusy(false); }
  };
  const roleMeta = ROLES.find((r) => r.value === form.role);
  return (
    <form onSubmit={submit} className="bg-white border border-[#E8E1D2] p-6" data-testid="add-user-form">
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680]">Add a login</div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls} htmlFor="new-user-email">Work email</label>
          <input id="new-user-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="name@marksmanhealthcare.com" data-testid="add-user-email" />
        </div>
        <div>
          <label className={labelCls} htmlFor="new-user-name">Name</label>
          <input id="new-user-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Rajneesh S." data-testid="add-user-name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="new-user-role">Role</label>
          <select id="new-user-role" value={form.role} onChange={set("role")} className={inputCls} data-testid="add-user-role">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {roleMeta && <p className="mt-1.5 text-[11.5px] text-[#5C6470] leading-snug">{roleMeta.hint}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="new-user-password">Password · min 8 characters</label>
          <input id="new-user-password" type="text" required minLength={8} value={form.password} onChange={set("password")} className={`${inputCls} font-mono`} placeholder="Share this with them" autoComplete="off" data-testid="add-user-password" />
        </div>
      </div>
      <label className="mt-4 flex items-start gap-3 cursor-pointer select-none" data-testid="add-user-outreach-label">
        <input type="checkbox" checked={form.outreach_access} onChange={set("outreach_access")} className="mt-0.5 accent-[#C84C21]" data-testid="add-user-outreach" />
        <span className="text-[12.5px] text-[#0F1825] leading-snug"><span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em]"><Send size={11} /> Prospect Outreach access</span><br />
          <span className="text-[#5C6470]">Can open /outreach: upload prospects, review and approve drafts, start and pause sending. Admins always have it.</span></span>
      </label>
      <div className="mt-5 flex justify-end">
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] disabled:opacity-50 text-white text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 transition-colors" data-testid="add-user-submit">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create login
        </button>
      </div>
    </form>
  );
}

function UserRow({ u, isSelf, onChanged, onDeleted }) {
  const [mode, setMode] = useState(null); // null | "password" | "delete"
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const short = u.email.split("@")[0];

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/auth/users/${u.id}`, body);
      toast.success(okMsg);
      onChanged(data);
      setMode(null);
      setPw("");
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/auth/users/${u.id}`);
      toast.success(`${u.email} removed`);
      onDeleted(u.id);
    } catch (err) { toast.error(errText(err)); setBusy(false); }
  };
  const act = "inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors disabled:opacity-40";

  return (
    <li className="bg-white border border-[#E8E1D2] px-5 py-4" data-testid={`user-row-${short}`}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-serif text-[1.05rem] text-[#0F1825]">{u.name}</span>
            <span className="text-[13px] text-[#5C6470] break-all" data-testid={`user-email-${short}`}>{u.email}</span>
            {isSelf && <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#A09680]">you</span>}
          </div>
          <div className="mt-1 font-mono text-[10px] text-[#A09680]">
            Added {fmtDate(u.created_at)} · last login {fmtDate(u.last_login_at)}{u.source === "seed" ? " · provisioned from server config" : ""}
          </div>
        </div>
        <select
          value={u.role}
          disabled={busy || isSelf}
          onChange={(e) => patch({ role: e.target.value }, `${u.name} is now ${e.target.value}`)}
          className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] bg-white ${ROLE_CLS[u.role] || ""} disabled:opacity-70`}
          data-testid={`user-role-${short}`}
          title={isSelf ? "You cannot change your own role" : "Change role"}
        >
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.value}</option>)}
        </select>
        <label className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] select-none ${(u.role === "admin" || u.role === "bd") ? "opacity-60" : "cursor-pointer"}`} title={(u.role === "admin" || u.role === "bd") ? "This role always has Prospect Outreach" : "Grant or revoke Prospect Outreach (/outreach)"} data-testid={`user-outreach-label-${short}`}>
          <input type="checkbox" checked={!!u.outreach_access} disabled={busy || u.role === "admin" || u.role === "bd"} onChange={(e) => patch({ outreach_access: e.target.checked }, `${u.name} ${e.target.checked ? "can now open" : "no longer sees"} Prospect Outreach`)} className="accent-[#C84C21]" data-testid={`user-outreach-${short}`} />
          <Send size={11} /> Outreach
        </label>
        <span className="inline-flex items-center gap-4">
          <button type="button" disabled={busy} onClick={() => setMode(mode === "password" ? null : "password")} className={`${act} text-[#0F1825] hover:text-[#C84C21]`} data-testid={`user-reset-${short}`}>
            <KeyRound size={12} /> Reset password
          </button>
          {!isSelf && (
            <button type="button" disabled={busy} onClick={() => setMode(mode === "delete" ? null : "delete")} className={`${act} text-[#A09680] hover:text-[#C84C21]`} data-testid={`user-delete-${short}`}>
              <Trash2 size={12} /> Remove
            </button>
          )}
        </span>
      </div>

      {mode === "password" && (
        <form
          onSubmit={(e) => { e.preventDefault(); patch({ password: pw }, `Password updated for ${u.email}`); }}
          className="mt-3 flex flex-wrap items-end gap-3 border-t border-[#F0EBDC] pt-3"
          data-testid={`user-reset-form-${short}`}
        >
          <div className="flex-1 min-w-[240px]">
            <label className={labelCls} htmlFor={`pw-${u.id}`}>New password · min 8 characters</label>
            <input id={`pw-${u.id}`} type="text" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} className={`${inputCls} font-mono`} autoComplete="off" data-testid={`user-reset-input-${short}`} />
          </div>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] disabled:opacity-50 text-white text-[10px] font-mono uppercase tracking-[0.18em] px-3.5 py-2.5 transition-colors" data-testid={`user-reset-submit-${short}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} Set password
          </button>
          <button type="button" onClick={() => { setMode(null); setPw(""); }} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] hover:text-[#0F1825] py-2.5">Cancel</button>
        </form>
      )}

      {mode === "delete" && (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-[#F0EBDC] pt-3" data-testid={`user-delete-confirm-${short}`}>
          <span className="text-[12.5px] text-[#C84C21]">Remove {u.email}? They will no longer be able to log in.</span>
          <button type="button" disabled={busy} onClick={remove} className="inline-flex items-center gap-2 bg-[#C84C21] hover:bg-[#0F1825] disabled:opacity-50 text-white text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-2 transition-colors" data-testid={`user-delete-yes-${short}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Yes, remove
          </button>
          <button type="button" onClick={() => setMode(null)} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] hover:text-[#0F1825]">Cancel</button>
        </div>
      )}
    </li>
  );
}

export default function ConsoleUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/auth/users`)
      .then(({ data }) => setItems(data))
      .catch((e) => setErr(e?.response?.status === 403 ? "Admin access required." : "Could not load users."));
  }, []);

  const upsert = (u) => setItems((list) => {
    const i = (list || []).findIndex((x) => x.id === u.id);
    if (i === -1) return [...(list || []), u];
    const next = [...list]; next[i] = u; return next;
  });
  const remove = (id) => setItems((list) => (list || []).filter((x) => x.id !== id));

  return (
    <ConsoleShell hideIllustrativeRibbon breadcrumb={<Breadcrumb items={[{ label: "Engagements", to: "/console/engagements" }, { label: "Manage users" }]} />}>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="page-console-users">
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">Admin · access</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0F1825] mt-3 leading-[1.1] tracking-tight">Manage users.</h1>
          <p className="mt-4 text-[#5C6470] text-[15px] leading-relaxed max-w-2xl">
            Create logins for your team, set what they can do, reset a password, or remove access. Changes apply immediately — no redeploy needed.
          </p>
        </div>

        <AddUserForm onCreated={upsert} />

        {err && <div className="mt-6 text-[#C84C21] text-[13px]" data-testid="users-error">{err}</div>}
        {!items && !err && <div className="mt-8 flex items-center gap-2 text-[#5C6470] text-[13px]"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {items && (
          <>
            <div className="mt-8 mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680] flex items-center gap-2" data-testid="users-count">
              <Users size={12} /> {items.length} account{items.length === 1 ? "" : "s"}
              <span className="ml-3 inline-flex items-center gap-2">{["admin", "operator", "bd", "reviewer"].map((r) => <RoleChip key={r} role={`${items.filter((u) => u.role === r).length} ${r}`} />)}</span>
            </div>
            <ul className="space-y-2">
              {items.map((u) => (
                <UserRow key={u.id} u={u} isSelf={u.id === user?.id} onChanged={upsert} onDeleted={remove} />
              ))}
            </ul>
          </>
        )}
      </div>
    </ConsoleShell>
  );
}
